import { Edit2, Trash2 } from 'lucide-react';
import type { Schedule } from '../../api/schedule';
import { formatDateIST, formatTimeIST } from '../../utils/function';
import Modal from '../model/Modal';
import { cn } from '../ui/utils';
import { useEffect, useState } from 'react';
import { ScheduleAudioPlayer } from '../songs/CustomAudioPlayer';

interface ViewScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  schedule: Schedule | null;
  bookingStatus?: string; 
}

export default function ViewScheduleModal({ isOpen, onClose, schedule, onEdit, onDelete, bookingStatus }: ViewScheduleModalProps) {
  
  const [isMp3Exists, setIsMp3Exists] = useState<boolean | null>(null);
  const [checkingMp3, setCheckingMp3] = useState(false);
  
  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete();
    }
  };
  
  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit();
    }
  };

  const mp3Exists=async()=>{
    if (!schedule) return;
    setCheckingMp3(true);
    setIsMp3Exists(null);

    try {
      const res = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/stream/schedule/${schedule.id}/exists`
      );

      const data = await res.json();
      setIsMp3Exists(data.exists);
    } catch {
      setIsMp3Exists(false);
    } finally {
      setCheckingMp3(false);
    }
  }

  useEffect(()=>{
      mp3Exists()
  },[schedule])

  if (!schedule) return null;
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Schedule Details"
      maxWidth="max-w-2xl"
      footer={
        <div className="flex justify-end space-x-3">
          {onDelete && (
            <button
              onClick={handleDelete}
              className="px-4 py-2 text-sm font-medium text-red-400 hover:text-white hover:bg-red-500/10 rounded-md transition-colors flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              <span>Delete</span>
            </button>
          )}
          {onEdit && (bookingStatus != "completed") && (
            <button
              onClick={handleEdit}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors flex items-center gap-2"
            >
              <Edit2 className="w-4 h-4" />
              <span>Edit</span>
            </button>
          )}
        </div>
      }
    >
      <div className="space-y-6">
        {/* Show Details */}
        <div className="bg-slate-800/50 rounded-lg p-6">
          <h3 className="text-lg font-medium text-white mb-4">Show Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <p className="text-sm text-slate-400">Show Name</p>
              <p className="text-white font-medium">{schedule.show?.name || 'N/A'}</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-slate-400">Host</p>
              <p className="text-white font-medium">{schedule.show?.hostName || 'N/A'}</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-slate-400">Categories</p>
              <div className="flex flex-wrap gap-2">
                {schedule.show?.categories?.map((cat, i) => (
                  <span key={i} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-700 text-slate-200">
                    {cat?.name || 'Uncategorized'}
                  </span>
                )) || 'N/A'}
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-slate-400">Status</p>
              <span className={cn(
                `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium`,
                schedule.status === 'live' && 'bg-red-500/20 text-red-400',
                schedule.status === 'upcoming' && 'bg-amber-500/20 text-amber-400',
                schedule.status === 'completed' && 'bg-green-500/20 text-green-400',
                schedule.status === 'cancelled' && 'bg-red-500/20 text-red-400',
                schedule.status === 'not_started' && 'bg-blue-500/20 text-blue-400'
              )}>
                {schedule.status?.charAt(0).toUpperCase() + schedule.status?.slice(1).toLowerCase()}
              </span>
            </div>
          </div>
        </div>

        {/* Schedule Details */}
        <div className="bg-slate-800/50 rounded-lg p-6">
          <h3 className="text-lg font-medium text-white mb-4">Schedule Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <p className="text-sm text-slate-400">Date</p>
              <p className="text-white font-medium">
                {schedule.date ? formatDateIST(schedule.date) : 'N/A'}
              </p>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-slate-400">Time</p>
              <p className="text-white font-medium">
                {schedule.startTime && schedule.endTime
                  ? `${formatTimeIST(schedule.startTime)} - ${formatTimeIST(schedule.endTime)}`
                  : 'N/A'}
              </p>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-slate-400">Recurring</p>
              <p className="text-white font-medium">
                {schedule.days?.length === 7 ? 'Every Day' :
                  schedule.days?.length ? schedule.days.join(', ') : 'No'}
              </p>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-slate-400">Special Event</p>
              <p className="text-white font-medium">
                {schedule.show.isSpecial ? 'Yes' : 'No'}
              </p>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-slate-400">Special Event</p>
              <p className="text-white font-medium">
                {schedule.show.isFeatured ? 'Yes' : 'No'}
              </p>
            </div>
          </div>
        </div>

        {/* Description */}
        {schedule.show?.description && (
          <div className="bg-slate-800/50 rounded-lg p-6">
            <h3 className="text-lg font-medium text-white mb-2">Description</h3>
            <p className="text-slate-300 text-sm">{schedule.show.description}</p>
          </div>
        )}

          <div className="border-t border-white/10 pt-4">
            {checkingMp3 ? (
              <p className="text-white/70 text-sm">Checking audio...</p>
            ) : isMp3Exists ? (
              <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                <p className="text-white/70 text-xs mb-2">
                  ▶ Now Playing Stream
                </p>
  
                <ScheduleAudioPlayer
                  src={`${import.meta.env.VITE_API_BASE_URL}/stream/schedule/${schedule.id}`}
                  currentPlaying={true}
                  setPlaying={()=>{}}
                />
  
              </div>
            ) : (
              <p className="text-white/70 text-sm">
                No MP3 available for this schedule
              </p>
            )}
          </div>

      </div>
    </Modal>
  );
}
